import { calculateFileSize } from '@pba/utils';
import { type FileAsset, FileType, type FolderAsset } from 'models';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  type ColumnsType,
  Delete2LineIcon,
  Dropdown,
  FileIconDeprecated,
  Flex,
  Icon,
  Modal,
  type ModalOptions,
  More2LineIcon,
  PencilLineIcon,
  Row,
  type TableSorterResult,
  Text,
  message,
  useColumnWithMoreActionsCustomTrigger,
} from 'ui';
import { handleAxiosError } from 'common/error-handlers';
import { openInNewWindow } from 'common/utils';
import { AssetsFilePath } from '../assets-file-path';
import { useAssetsQuery, useDeleteAssetMutation } from '../queries';
import type { AssetsQueryFilter } from '../queries/keys';
import type { AssetsSorter } from '../types';
import { AssetsRenameModal, type AssetsRenameModalOptions } from './assets-rename-modal';
import { AssetsTableContent } from './assets-table-content';

const pageSize = 10;

export type AssetItem = FileAsset | FolderAsset;

type P = {
  canManage: boolean;
  filter: Pick<AssetsQueryFilter, 'communityId' | 'programId'>;
  folders: FolderAsset[];
  onNavigateToParentFolder: (folder: FolderAsset) => void;
  onNavigateToRoot: () => void;
  onOpenFolder: (folder: FolderAsset) => void;
  parentFolder?: FolderAsset;
};

const AssetsTable = ({
  canManage,
  filter,
  folders,
  onNavigateToParentFolder,
  onNavigateToRoot,
  onOpenFolder,
  parentFolder,
}: P) => {
  const { t } = useTranslation();
  const [sorter, setSorter] = useState<AssetsSorter>({});
  const [renameOptions, setRenameOptions] = useState<ModalOptions<AssetsRenameModalOptions>>({
    isOpened: false,
  });
  const [modal, contextHolder] = Modal.useModal();
  const { mutateAsync: deleteAsset } = useDeleteAssetMutation();
  const columnWithMoreActions = useColumnWithMoreActionsCustomTrigger<AssetItem>();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading: isLoadingFiles,
  } = useAssetsQuery({
    filter: { ...filter, parentId: parentFolder?.id },
    pagination: { pageSize },
    sorter,
  });

  const files = useMemo(() => data?.pages.flat() ?? [], [data]);

  const onChange = (tableSorter: TableSorterResult<AssetItem>) => {
    if (Array.isArray(tableSorter) || !tableSorter.order) {
      setSorter({});
      return;
    }

    if (tableSorter.field === undefined) {
      setSorter({});
      return;
    }

    switch (tableSorter.field) {
      case 'name':
      case 'size':
      case 'updatedAt':
        setSorter({ [tableSorter.field]: tableSorter.order });
        return;
      default:
        setSorter({});
    }
  };

  const onDeleteFile = (record: AssetItem) => {
    const isFile = record.type === FileType;

    modal.confirm({
      content: (
        <Trans
          components={{ strong: <Text strong /> }}
          // TODO: Detect whether the folder is empty and use a specific confirmation instead of this temporary message.
          i18nKey={
            isFile
              ? 'Are you sure you want to delete <strong>{{name}}</strong>?'
              : 'Folder <strong>{{name}}</strong> might not be empty. Are you sure you want to delete it?'
          }
          values={{ name: record.name }}
        />
      ),
      icon: <Delete2LineIcon type="error" />,
      okText: 'Delete',
      async onOk() {
        await deleteAsset(
          {
            id: record.id,
          },
          {
            onError(error) {
              handleAxiosError(
                error,
                isFile
                  ? "Something went wrong. File couldn't be deleted."
                  : "Something went wrong. Folder couldn't be deleted.",
              );
            },
            onSuccess() {
              message.success(isFile ? 'File deleted successfully' : 'Folder deleted successfully');
            },
          },
        );
      },
      title: isFile ? 'Delete file' : 'Delete folder',
      type: 'error',
    });
  };

  const actionsMenu = (record: AssetItem) => [
    ...(record.type === FileType
      ? [
          {
            icon: <Icon icon="download-2-line" />,
            key: 'download',
            label: t('Download'),
            onClick() {
              // TODO: Open the system save dialog instead of saving straight to the browser downloads folder.
              openInNewWindow(record.downloadUrl);
            },
          },
        ]
      : []),
    {
      icon: <PencilLineIcon />,
      key: 'rename',
      label: t('Rename'),
      onClick() {
        setRenameOptions({
          editingFile: record,
          isOpened: true,
        });
      },
    },
    {
      danger: true,
      icon: <Delete2LineIcon />,
      key: 'delete',
      label: t('Delete'),
      onClick() {
        onDeleteFile(record);
      },
    },
  ];

  const onRow = (record: AssetItem) => ({
    onClick() {
      if (record.type !== FileType) {
        onOpenFolder(record);
        return;
      }

      openInNewWindow(record.downloadUrl);
    },
  });

  const columns: ColumnsType<AssetItem> = [
    {
      dataIndex: 'name',
      render: (_: unknown, record: AssetItem) => (
        <Row align="middle" gutter="xs" justify="start">
          <Col>
            <FileIconDeprecated asset={record} size="extra-extra-small" />
          </Col>
          <Col>
            {record.name}
            {record.type === FileType && record.extension}
          </Col>
        </Row>
      ),
      sorter: true,
      sortOrder: sorter.name,
      title: t('Name'),
    },
    {
      dataIndex: 'updatedAt',
      render: (item: Date) => t('dateFormats.dateTime', { date: item }),
      sorter: true,
      sortOrder: sorter.updatedAt,
      title: t('Modified'),
    },
    {
      dataIndex: 'size',
      render: (_: unknown, record: AssetItem) => (record.type === FileType ? calculateFileSize(record.size) : ''),
      sorter: true,
      sortOrder: sorter.size,
      title: t('Size'),
    },
    {
      align: 'center',
      dataIndex: 'actions',
      ...columnWithMoreActions({
        trigger: ({ record }: { record: AssetItem }) =>
          canManage ? (
            <Dropdown menu={{ items: actionsMenu(record) }} placement="bottomRight" trigger={['click']}>
              <Button
                icon={<More2LineIcon />}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                size="small"
                type="link-primary"
              />
            </Dropdown>
          ) : null,
      }),
    },
  ];

  return (
    <>
      {contextHolder}
      <Flex gap="ms" vertical>
        <AssetsFilePath
          folders={folders}
          onNavigateToParentFolder={onNavigateToParentFolder}
          onNavigateToRoot={onNavigateToRoot}
        />
        <AssetsTableContent
          canManage={canManage}
          columns={columns}
          communityId={filter.communityId}
          fetchNextPage={fetchNextPage}
          files={files}
          hasNextPage={hasNextPage}
          isFetching={isFetching}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoadingFiles}
          onChange={onChange}
          onRow={onRow}
          parentId={parentFolder?.id}
          programId={filter.programId}
        />
      </Flex>
      <AssetsRenameModal
        onCancel={() => {
          setRenameOptions({ isOpened: false });
        }}
        options={renameOptions}
      />
    </>
  );
};

export default AssetsTable;
